// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import { listShareholders } from '../../../../../../services/ShareholderApiClient';
import { queueVoteRoomPrelaunchEmailVerification } from '../../../../../../services/AnalysisApiClient';
import { pushToast } from '../../../../../../utils/toast/toastStore';
import {
  isAllPrecheckVerified,
  PRECHECK_POLL_INTERVAL_MS,
  PRECHECK_POLL_MAX_MS,
  PRECHECK_RESOLVED_STATUSES,
} from '../shared/voteRoomConstants';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

/**
 * Pre-launch email verification: queue send, poll for Sent status (max 30s), manual refresh/resend.
 */
export function useVoteRoomPrecheck({ analysisId, toLocalShareholder, setShareholders, refreshShareholders }) {
  const [precheckSending, setPrecheckSending] = useState(false);
  const [precheckPolling, setPrecheckPolling] = useState(false);
  const [precheckRefreshing, setPrecheckRefreshing] = useState(false);
  const [precheckPollTimedOut, setPrecheckPollTimedOut] = useState(false);
  const [precheckError, setPrecheckError] = useState(null);
  const pollCancelRef = useRef(null);

  const cancelActivePoll = useCallback(() => {
    if (pollCancelRef.current) pollCancelRef.current.cancelled = true;
  }, []);

  useEffect(() => () => cancelActivePoll(), [cancelActivePoll]);

  const resetPrecheckUi = useCallback(() => {
    cancelActivePoll();
    setPrecheckSending(false);
    setPrecheckPolling(false);
    setPrecheckRefreshing(false);
    setPrecheckPollTimedOut(false);
    setPrecheckError(null);
  }, [cancelActivePoll]);

  const pollPrecheckDeliveryStatus = useCallback(async () => {
    if (!analysisId) return;

    cancelActivePoll();
    const cancelToken = { cancelled: false };
    pollCancelRef.current = cancelToken;

    setPrecheckPolling(true);
    setPrecheckPollTimedOut(false);

    let lastEligible = [];
    let consecutiveErrors = 0;
    const deadline = Date.now() + PRECHECK_POLL_MAX_MS;

    while (Date.now() < deadline && !cancelToken.cancelled) {
      try {
        const rows = await listShareholders(analysisId);
        if (cancelToken.cancelled) break;

        consecutiveErrors = 0;
        setShareholders(rows.map(toLocalShareholder));
        lastEligible = rows.filter((s) => !s.excluded);
        const allResolved =
          lastEligible.length === 0 ||
          lastEligible.every((s) => PRECHECK_RESOLVED_STATUSES.includes(s.emailPrecheckStatus || ''));
        if (allResolved) break;
      } catch (error) {
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS && !cancelToken.cancelled) {
          const detail = error instanceof Error ? error.message : '';
          setPrecheckError(
            detail
              ? `Unable to refresh email check status: ${detail} Use Check Status to retry.`
              : 'Unable to refresh email check status. Use Check Status to retry.'
          );
          break;
        }
      }

      if (Date.now() >= deadline || cancelToken.cancelled) break;
      await sleep(PRECHECK_POLL_INTERVAL_MS);
    }

    if (cancelToken.cancelled) return;

    setPrecheckPolling(false);
    if (!isAllPrecheckVerified(lastEligible)) setPrecheckPollTimedOut(true);
  }, [analysisId, cancelActivePoll, setShareholders, toLocalShareholder]);

  const handleRefreshPrecheckStatus = useCallback(async () => {
    setPrecheckError(null);
    setPrecheckRefreshing(true);
    try {
      const rows = await refreshShareholders();
      const eligible = rows.filter((s) => !s.excluded);
      if (isAllPrecheckVerified(eligible)) setPrecheckPollTimedOut(false);
    } finally {
      setPrecheckRefreshing(false);
    }
  }, [refreshShareholders]);

  const handleSendPrecheck = useCallback(async () => {
    if (!analysisId) {
      setPrecheckError('Analysis is not available for pre-launch email check.');
      return;
    }
    setPrecheckError(null);
    setPrecheckPollTimedOut(false);
    setPrecheckSending(true);
    let queuedCount = 0;
    try {
      const result = await queueVoteRoomPrelaunchEmailVerification(analysisId);
      queuedCount = result.queuedCount;
      await refreshShareholders();
      const skippedConfirmed = result.skippedConfirmedCount ?? 0;
      if (result.failedCount > 0 || result.invalidEmailCount > 0) {
        setPrecheckError(
          `Queued ${result.queuedCount}/${result.eligibleCount}. Fix ${result.failedCount + result.invalidEmailCount} shareholder email checks and retry.`
        );
      } else if (result.queuedCount === 0 && skippedConfirmed > 0) {
        pushToast(
          `All ${skippedConfirmed} eligible shareholder(s) are already confirmed; nothing new was queued.`,
          'success'
        );
      } else if (skippedConfirmed > 0) {
        pushToast(
          `Queued pre-launch email checks for ${result.queuedCount} shareholder(s); ${skippedConfirmed} already confirmed.`,
          'success'
        );
      } else {
        pushToast(`Queued pre-launch email checks for ${result.queuedCount} shareholder(s).`, 'success');
      }
    } catch (error) {
      setPrecheckError(error instanceof Error ? error.message : 'Failed to queue pre-launch email checks.');
    } finally {
      setPrecheckSending(false);
    }
    if (queuedCount > 0) {
      await pollPrecheckDeliveryStatus();
    }
  }, [analysisId, pollPrecheckDeliveryStatus, refreshShareholders]);

  return {
    precheckSending,
    precheckPolling,
    precheckRefreshing,
    precheckPollTimedOut,
    precheckError,
    handleSendPrecheck,
    handleRefreshPrecheckStatus,
    resetPrecheckUi,
  };
}
